import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Upload, Loader2, Check, AlertTriangle } from 'lucide-react';
import { apiBase, authHeaders, hasOrgContext } from '../utils/authHeaders';

/**
 * Canonical invoice intake UI for the new pipeline (P1-6).
 *
 * Posts multipart to POST /api/documents/bulk — same path as email/Nextcloud
 * intake. Review happens on /review (not the deprecated /api/ingestion/jobs
 * or /api/costing/import surfaces).
 */

export function buildBulkFormData(fileList) {
  const fd = new FormData();
  for (const f of fileList || []) {
    fd.append('files', f, f.name || 'upload.bin');
  }
  return fd;
}

export default function PipelineBulkUpload({ onUploaded, compact = false }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const upload = async () => {
    if (!files.length) return;
    if (!hasOrgContext()) {
      setError('No active organization — reload or pick an organization.');
      return;
    }
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const headers = authHeaders();
      delete headers['Content-Type'];
      const res = await fetch(`${apiBase()}/api/documents/bulk`, {
        method: 'POST',
        headers,
        body: buildBulkFormData(files),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = body.detail;
        setError(
          typeof detail === 'string'
            ? detail
            : Array.isArray(detail)
              ? detail.map((d) => d.msg || JSON.stringify(d)).join('; ')
              : res.statusText || `HTTP ${res.status}`,
        );
        return;
      }
      setResult(body);
      setFiles([]);
      if (typeof onUploaded === 'function') onUploaded(body);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={
        compact
          ? 'bg-white border border-slate-200 rounded-lg p-4'
          : 'bg-surface border border-surface-border rounded-lg p-5'
      }
      data-testid="pipeline-bulk-upload"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">
            Upload invoices (pipeline)
          </h2>
          <p className="text-xs text-slate-600 mt-0.5">
            PDF / JPG / PNG or a zip. Lands in the review queue after extraction —
            same path as email intake.
          </p>
        </div>
        <Upload className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.zip,application/pdf,image/*,application/zip"
          onChange={(e) => setFiles(Array.from(e.target.files || []))}
          className="text-xs"
          data-testid="pipeline-bulk-file-input"
        />
        <button
          type="button"
          disabled={!files.length || busy}
          onClick={upload}
          className="px-3 py-1.5 rounded-md text-sm bg-emerald-700 text-white disabled:opacity-40"
          data-testid="pipeline-bulk-upload-btn"
        >
          {busy ? (
            <Loader2 className="w-4 h-4 inline animate-spin mr-1" />
          ) : (
            <Upload className="w-4 h-4 inline mr-1" />
          )}
          Upload {files.length ? `(${files.length})` : ''}
        </button>
        <Link
          to="/review"
          className="text-xs text-emerald-800 underline"
          data-testid="pipeline-bulk-review-link"
        >
          Open review queue
        </Link>
      </div>

      {error && (
        <div
          className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 flex gap-2"
          data-testid="pipeline-bulk-error"
        >
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {result && (
        <div
          className="mt-3 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-3 py-2 flex gap-2"
          data-testid="pipeline-bulk-ok"
        >
          <Check className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>
            Queued {result.created ?? 0} document(s)
            {result.duplicates?.length || result.duplicate_ids?.length
              ? ` · ${result.duplicates?.length || result.duplicate_ids.length} duplicate(s) skipped`
              : ''}
            . Extraction runs in the background — refresh the review queue shortly.
          </span>
        </div>
      )}
    </div>
  );
}
